import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedError } from 'errors/types';
import type { RootState } from 'state/store';

type HeaderLink = {
  text: string;
  href: string;
  alt?: string;
  type?: 'text' | 'direct';
};
type HeaderSection = {
  heading: string;
  alt: string;
  href: string;
  links: HeaderLink[];
};
type HeaderSecondaryBanner = {
  text: string;
  link: HeaderLink;
};

type HeaderQuickLink = {
  heading: string;
  href: string;
  links: HeaderLink[];
};

type HeaderMenuBanner = {
  text: string;
  link: HeaderLink;
};

export type HeaderConfigData = {
  hideA11yBtn: boolean;
  'menu-banner': HeaderMenuBanner[];
  'secondary-banner': HeaderSecondaryBanner[];
  sections: Array<HeaderSection[]>;
  'quick-links': Array<HeaderQuickLink[]>;
};

export type HeaderConfigState = {
  isLoading: boolean;
  errors: null | NormalizedError[];
  data: HeaderConfigData;
};

export const initialState: HeaderConfigState = {
  isLoading: false,
  errors: null,
  data: {
    hideA11yBtn: false,
    'menu-banner': [],
    'secondary-banner': [],
    sections: [],
    'quick-links': []
  }
};

export const slice = createSlice({
  name: 'headerConfig',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.isLoading = initialState.isLoading;
      state.errors = initialState.errors;
      state.data = initialState.data;
    },
    setIsLoading: (state) => {
      state.isLoading = true;
      state.errors = initialState.errors;
    },
    setSuccess: (state, { payload }: PayloadAction<HeaderConfigData>) => {
      state.isLoading = false;
      state.data = payload;
      state.errors = initialState.errors;
    },

    setError: (state, { payload }) => {
      state.isLoading = false;
      state.errors = payload;
    }
  }
});

export const {
  setInitialState,
  setIsLoading,
  setSuccess,
  setError
} = slice.actions;

export const selectSlice = (state: RootState) => state[slice.name];
export const selectIsLoading = (state: RootState) =>
  selectSlice(state).isLoading;
export const selectErrors = (state: RootState) => selectSlice(state).errors;
export const selectData = (state: RootState) => selectSlice(state).data;

export default slice.reducer;
